import React, { useEffect, useRef, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, FileText, Trash, Download } from "@phosphor-icons/react";

const CATS = [
  { v: "prescription", l: "Prescription" },
  { v: "scan", l: "Scan / Ultrasound" },
  { v: "lab", l: "Lab Report" },
  { v: "doctor_note", l: "Doctor's Note" },
  { v: "insurance", l: "Insurance" },
  { v: "other", l: "Other" },
];

export default function MedicalRecords() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ title: "", category: "prescription", record_date: new Date().toISOString().slice(0, 10) });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = getToken();

  const load = async () => { const r = await api.get("/health/records"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const qs = new URLSearchParams({ title: meta.title || file.name, category: meta.category, record_date: meta.record_date }).toString();
      await api.post(`/health/records/upload?${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Record saved.");
      setMeta({ ...meta, title: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const del = async (id) => { await api.delete(`/health/records/${id}`); load(); };

  return (
    <div className="space-y-8" data-testid="records-page">
      <header>
        <p className="overline text-saffron mb-2">Vault</p>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Medical records</h1>
        <p className="text-ink-700 mt-2">Prescriptions, scans, lab reports — your private archive.</p>
      </header>

      <div className="vansha-card p-6 grid sm:grid-cols-4 gap-4 items-end" data-testid="record-uploader">
        <div className="sm:col-span-2"><label className="overline mb-2 block">Title</label><input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="input-line" placeholder="20-week anomaly scan" data-testid="rec-title" /></div>
        <div><label className="overline mb-2 block">Category</label>
          <select value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} className="input-line bg-transparent" data-testid="rec-category">
            {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div><label className="overline mb-2 block">Date</label><input type="date" value={meta.record_date} onChange={(e) => setMeta({ ...meta, record_date: e.target.value })} className="input-line" data-testid="rec-date" /></div>
        <input ref={fileRef} type="file" onChange={upload} className="hidden" id="record-file" data-testid="rec-file-input" />
        <label htmlFor="record-file" className={`btn-primary cursor-pointer sm:col-span-4 sm:w-auto sm:justify-self-end ${uploading ? "opacity-60" : ""}`} data-testid="rec-upload-btn">
          <UploadSimple weight="thin" size={18} /> {uploading ? "Uploading…" : "Upload"}
        </label>
      </div>

      {items.length === 0 ? (
        <div className="vansha-card p-12 text-center text-ink-500" data-testid="records-empty">
          <FileText weight="thin" size={48} className="mx-auto text-saffron mb-4" />
          <p className="font-serif text-2xl text-ink-900 mb-2">Your vault is empty.</p>
          <p>Upload your first prescription or scan.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="vansha-card p-5 flex items-start gap-4" data-testid={`rec-${r.id}`}>
              <div className="w-10 h-10 rounded-full bg-vansha-accent flex items-center justify-center"><FileText weight="thin" size={20} className="text-terracotta" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-xl">{r.title}</h3>
                <p className="text-sm text-ink-500 mt-1 capitalize">{r.category.replace("_", " ")} · {r.record_date}</p>
              </div>
              <a href={`${API_BASE}/health/records/file/${r.id}?auth=${token}`} target="_blank" rel="noreferrer" className="btn-ghost p-2" data-testid={`rec-view-${r.id}`}><Download weight="thin" size={18} /></a>
              <button onClick={() => del(r.id)} className="btn-ghost p-2" data-testid={`rec-del-${r.id}`}><Trash weight="thin" size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
